import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';

import { CompaniesService } from '../../features/companies/data-access/companies.service';

export interface TenantBrandingVm {
  companyId: string | null;
  slug: string;
  displayName: string;
  legalName: string;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  slotAvailableColor: string;
  slotUnavailableColor: string;
  slotPressureColor: string;
  slotPastPatternColor: string;
  slotPastPatternOpacity: number;
  sidebarBackgroundColor: string;
  sidebarTextColor: string;
  sidebarBackgroundOverlayOpacity: number;
  sidebarBackgroundImageUrl: string | null;
  contentBackgroundOverlayOpacity: number;
  contentBackgroundImageUrl: string | null;
  topbarBackgroundOverlayOpacity: number;
  topbarBackgroundImageUrl: string | null;
  showCompanyNameInHeader: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class TenantBrandingService {
  private readonly companiesService = inject(CompaniesService);
  private readonly cache = new Map<string, BehaviorSubject<TenantBrandingVm | null>>();

  public getBranding(slug: string | null | undefined): Observable<TenantBrandingVm | null> {
    const normalizedSlug = (slug ?? '').trim().toLowerCase();
    if (!normalizedSlug) {
      return of(null);
    }

    const cached = this.cache.get(normalizedSlug);
    if (cached) {
      return cached.asObservable();
    }

    const subject = this.createCacheEntry(normalizedSlug);
    this.companiesService.getBrandingBySlug(normalizedSlug).subscribe({
      next: (branding) => subject.next(branding),
      error: () => subject.next(this.buildFallbackBranding(normalizedSlug)),
    });

    return subject.asObservable();
  }

  public getBrandingByCompanyId(companyId: string | null | undefined): Observable<TenantBrandingVm | null> {
    const normalizedCompanyId = (companyId ?? '').trim();
    if (!normalizedCompanyId) {
      return of(null);
    }

    const cacheKey = `company:${normalizedCompanyId}`;
    const cached = this.cache.get(cacheKey);
    if (cached) {
      return cached.asObservable();
    }

    const subject = this.createCacheEntry(cacheKey);
    this.companiesService.getCurrentBranding(normalizedCompanyId).subscribe({
      next: (branding) => subject.next(branding),
      error: () => subject.next(null),
    });

    return subject.asObservable();
  }

  public toCssVars(branding: TenantBrandingVm | null): Record<string, string> {
    return {
      '--tenant-primary': branding?.primaryColor ?? '#2563eb',
      '--tenant-secondary': branding?.secondaryColor ?? '#0f172a',
      '--tenant-accent': branding?.accentColor ?? '#f59e0b',
      '--tenant-slot-available': branding?.slotAvailableColor ?? '#22c55e',
      '--tenant-slot-unavailable': branding?.slotUnavailableColor ?? '#f3f4f6',
      '--tenant-slot-pressure': branding?.slotPressureColor ?? '#f59e0b',
      '--tenant-slot-past-pattern': branding?.slotPastPatternColor ?? '#64748b',
      '--tenant-slot-past-pattern-opacity': this.toPercentValue(branding?.slotPastPatternOpacity ?? 20),
      '--tenant-sidebar-bg': branding?.sidebarBackgroundColor ?? branding?.secondaryColor ?? '#0f172a',
      '--tenant-sidebar-text': branding?.sidebarTextColor ?? '#ffffff',
      '--tenant-sidebar-overlay-opacity': this.toOpacityValue(branding?.sidebarBackgroundOverlayOpacity ?? 88),
      '--tenant-sidebar-bg-image': this.toCssImageValue(branding?.sidebarBackgroundImageUrl ?? null),
      '--tenant-content-overlay-opacity': this.toOpacityValue(branding?.contentBackgroundOverlayOpacity ?? 93),
      '--tenant-content-bg-image': this.toCssImageValue(branding?.contentBackgroundImageUrl ?? null),
      '--tenant-topbar-overlay-opacity': this.toOpacityValue(branding?.topbarBackgroundOverlayOpacity ?? 82),
      '--tenant-topbar-bg-image': this.toCssImageValue(branding?.topbarBackgroundImageUrl ?? null),
    };
  }

  public primeBranding(branding: TenantBrandingVm): void {
    const normalizedSlug = branding.slug.trim().toLowerCase();
    if (normalizedSlug) {
      this.createCacheEntry(normalizedSlug).next(branding);
    }

    const normalizedCompanyId = branding.companyId?.trim();
    if (normalizedCompanyId) {
      this.createCacheEntry(`company:${normalizedCompanyId}`).next(branding);
    }
  }

  private createCacheEntry(key: string): BehaviorSubject<TenantBrandingVm | null> {
    const cached = this.cache.get(key);
    if (cached) {
      return cached;
    }

    const subject = new BehaviorSubject<TenantBrandingVm | null>(null);
    this.cache.set(key, subject);
    return subject;
  }

  private buildFallbackBranding(slug: string): TenantBrandingVm {
    return {
      companyId: null,
      slug,
      displayName: this.humanizeSlug(slug),
      legalName: this.humanizeSlug(slug),
      logoUrl: null,
      primaryColor: '#2563eb',
      secondaryColor: '#0f172a',
      accentColor: '#f59e0b',
      slotAvailableColor: '#22c55e',
      slotUnavailableColor: '#f3f4f6',
      slotPressureColor: '#f59e0b',
      slotPastPatternColor: '#64748b',
      slotPastPatternOpacity: 20,
      sidebarBackgroundColor: '#0f172a',
      sidebarTextColor: '#ffffff',
      sidebarBackgroundOverlayOpacity: 88,
      sidebarBackgroundImageUrl: null,
      contentBackgroundOverlayOpacity: 93,
      contentBackgroundImageUrl: null,
      topbarBackgroundOverlayOpacity: 82,
      topbarBackgroundImageUrl: null,
      showCompanyNameInHeader: true,
    };
  }

  private toCssImageValue(url: string | null): string {
    const normalized = url?.trim() ?? '';
    if (!normalized) {
      return 'none';
    }

    const escaped = normalized.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    return `url("${escaped}")`;
  }

  private toOpacityValue(value: number): string {
    const normalized = Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 0;
    return (normalized / 100).toFixed(2);
  }

  private toPercentValue(value: number): string {
    const normalized = Number.isFinite(value) ? Math.min(100, Math.max(0, Math.round(value))) : 0;
    return `${normalized}%`;
  }

  private humanizeSlug(slug: string): string {
    return slug
      .split('-')
      .filter((part) => part.length > 0)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }
}
